import { Injectable, OnDestroy, signal, Signal, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { catchError, firstValueFrom, of } from 'rxjs';
import { openDB, IDBPDatabase } from 'idb';
import { environment } from '../environments/environment';
import {
  TenantModule,
  TenantModulesCacheEntry,
  TenantModulesResponse,
} from './tenant-module.types';

const DB_NAME = 'nuvertos-tenant-modules';
const DB_VERSION = 1;
const STORE = 'tenant-modules-store';
const ENTRY_KEY = 'modules';
const LEGACY_LS_KEY = 'nuvertos:tenant-modules';
const BC_NAME = 'nuvertos-modules';
const POLL_INTERVAL_MS = 15 * 60 * 1000;
const POLL_JITTER_MS = 60 * 1000;

interface TenantModulesDB {
  'tenant-modules-store': {
    key: string;
    value: TenantModulesCacheEntry;
  };
}

type ManifestPayload =
  | TenantModulesResponse
  | { data: TenantModulesResponse }
  | null;

/**
 * TenantModuleService is the frontend companion to `GET /api/v1/tenant/modules`.
 *
 * Lifecycle (split into two phases — they run from APP_INITIALIZER in each
 * app's `app.config.ts`, which inlines the factory rather than using a helper
 * from libs/auth; libs/auth is federation-shared but libs/shared is not, and
 * routing the inject through libs/auth produces a second TenantModuleService
 * instance whose signal updates never reach the host's sidebar):
 *
 *   1. `init()` — cache-first hydration. Reads any same-tenant entry from
 *      IndexedDB into the live `modules` signal, opens the cross-tab
 *      `BroadcastChannel`, and returns. **No HTTP. No polling.**
 *   2. `loadAfterLogin()` — gated on a confirmed auth session. Performs the
 *      first /api/v1/tenant/modules fetch, persists to IndexedDB, applies to
 *      the live signal, and (re)arms the polling loop. Also clears the
 *      `pollingStopped` flag set by a prior 401, so a fresh login revives
 *      polling.
 *
 * Update detection:
 *   - **Poll** (every 15 min ± 60 s) sends the cached ETag. 304 → noop. 200
 *     → save the new manifest to IDB **but do not mutate the live signal**
 *     (the user keeps working on the version they booted with), set
 *     `updateAvailable`, and broadcast the new ETag.
 *   - **BroadcastChannel** carries the new ETag from any tab that detects or
 *     applies an update. Sibling tabs whose ETag differs flip
 *     `updateAvailable` instantly without a network round-trip.
 *   - 401 from the poll stops further polling for the rest of the page life;
 *     the auth interceptor will redirect to `/login` and the next session
 *     starts fresh via `loadAfterLogin()`.
 *
 * Banner UX (consumer): subscribe to `updateAvailable`. When `true`, show a
 * "New version available — Refresh" prompt. The button just calls
 * `window.location.reload()`; the next boot's cache-first init() picks up
 * the new manifest from IDB.
 *
 * Consumers:
 *   - Components/templates read the reactive `modules` signal.
 *   - Guards, interceptors, template `*ngIf` use the sync `isEnabled(key)` check
 *     (backed by an in-memory map populated during init()/loadAfterLogin()).
 *   - Admin actions that change tenant config can call `refresh()` to force a
 *     refetch (also updates the live signal).
 *
 * Sub-modules:
 *   - Keys are dot-notated and globally unique: `vendor.po`, `accounting.gl`.
 *   - `isEnabled('vendor.po')` works identically to `isEnabled('vendor')`.
 *   - `parent_key` on the returned TenantModule tells you the hierarchy.
 *   - Invariant (enforced backend-side): if any sub-module is enabled for a
 *     tenant, its parent is also enabled. Frontend code can rely on this.
 */
@Injectable({ providedIn: 'root' })
export class TenantModuleService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly _modules = signal<TenantModule[]>([]);
  private readonly _updateAvailable = signal<boolean>(false);
  private readonly byKey = new Map<string, TenantModule>();
  private readonly dbPromise: Promise<IDBPDatabase<TenantModulesDB>> | null;
  private etag: string | null = null;
  private bc: BroadcastChannel | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingStopped = false;

  /** Reactive snapshot of the current enabled module list. */
  readonly modules: Signal<TenantModule[]> = this._modules.asReadonly();

  /** True once a fresher manifest is known (poll or sibling broadcast). */
  readonly updateAvailable: Signal<boolean> = this._updateAvailable.asReadonly();

  constructor() {
    this.dbPromise =
      typeof indexedDB !== 'undefined'
        ? openDB<TenantModulesDB>(DB_NAME, DB_VERSION, {
            upgrade(db) {
              if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE);
              }
            },
          })
        : null;
  }

  /**
   * Cache-only boot step. Hydrates the live signal from IndexedDB if a
   * same-tenant entry exists, opens the cross-tab BroadcastChannel, and
   * RETURNS — does NOT fetch and does NOT start polling. The auth-session
   * APP_INITIALIZER in each app's `app.config.ts` calls `loadAfterLogin()`
   * once a session is confirmed.
   *
   * Always resolves — caller should not reject-catch.
   */
  async init(): Promise<void> {
    this.migrateLegacyLocalStorage();
    await this.hydrateFromCache();
    this.openBroadcastChannel();
  }

  /**
   * Synchronous enabled-check. Safe to call from guards, HTTP interceptors,
   * and template bindings — reads from the in-memory map set during init()
   * or loadAfterLogin().
   */
  isEnabled(key: string): boolean {
    return this.byKey.has(key);
  }

  /** True if this module is top-level (has no parent). */
  isParent(module: TenantModule): boolean {
    return !module.parent_key;
  }

  /** True if this module is a sub-module of another. */
  isSubModule(module: TenantModule): boolean {
    return !!module.parent_key;
  }

  /** Returns the enabled children of a parent module (empty if none). */
  childrenOf(parentKey: string): TenantModule[] {
    return this.modules().filter((m) => m.parent_key === parentKey);
  }

  /**
   * Force a refetch and apply to the live signal. Intended for admin actions
   * that change tenant config and want the change reflected immediately
   * without a page reload.
   */
  async refresh(): Promise<void> {
    await this.fetchAndApply();
  }

  /**
   * Call after successful authentication (login form submit or session
   * restore) to seed IndexedDB and (re)arm polling. APP_INITIALIZER's init()
   * runs before login so the boot fetch path is not used; this method closes
   * the gap. Also un-kills polling that was stopped by a prior 401.
   */
  async loadAfterLogin(): Promise<void> {
    this.pollingStopped = false;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    await this.fetchAndApply();
    this.scheduleNextPoll();
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.bc) {
      this.bc.close();
      this.bc = null;
    }
  }

  // --- private: cache + fetch -----------------------------------------------

  private async hydrateFromCache(): Promise<boolean> {
    if (!this.dbPromise) return false;
    try {
      const db = await this.dbPromise;
      const entry = await db.get(STORE, ENTRY_KEY);
      if (!entry?.modules) return false;
      if (entry.tenant !== this.tenantFromHostname()) return false;
      this.setModules(entry.modules);
      this.etag = entry.etag ?? null;
      return true;
    } catch {
      return false;
    }
  }

  /** Fetch (no If-None-Match) and apply to the live signal + IDB + etag. */
  private async fetchAndApply(): Promise<void> {
    const result = await this.requestManifest({ useETag: false });
    if (result.kind === 'changed') {
      this.setModules(result.modules);
      this.etag = result.etag;
      await this.persist(result.modules, result.etag);
      return;
    }
    console.warn(
      `[TenantModuleService] fetchAndApply did not apply modules — kind=${result.kind}. ` +
        `Sidebar may stay empty until next successful fetch.`,
    );
  }

  /**
   * Conditional fetch used by polling. Saves to IDB on drift but does NOT
   * mutate the live `modules` signal — the user continues on the booted
   * version until they refresh.
   */
  private async pollAndStashIfChanged(): Promise<void> {
    if (this.pollingStopped) return;
    const result = await this.requestManifest({ useETag: true });
    if (result.kind === 'unauthorized') {
      this.pollingStopped = true;
      return;
    }
    if (result.kind === 'changed') {
      this.etag = result.etag;
      await this.persist(result.modules, result.etag);
      this._updateAvailable.set(true);
      this.bc?.postMessage({ etag: result.etag });
    }
    // 'not-modified' and 'error' → no-op; next poll will retry.
  }

  private async requestManifest(opts: {
    useETag: boolean;
  }): Promise<
    | { kind: 'changed'; modules: TenantModule[]; etag: string | null }
    | { kind: 'not-modified' }
    | { kind: 'unauthorized' }
    | { kind: 'error' }
  > {
    const url = `${environment.authUrl}/api/v1/tenant/modules`;
    let headers = new HttpHeaders();
    if (opts.useETag && this.etag) {
      headers = headers.set('If-None-Match', this.etag);
    }

    const response = await firstValueFrom(
      this.http
        .get<ManifestPayload>(url, {
          headers,
          observe: 'response',
          withCredentials: true,
        })
        .pipe(
          catchError((err: HttpErrorResponse) => {
            if (err.status === 304) {
              return of({ status: 304 } as unknown as HttpResponse<ManifestPayload>);
            }
            if (err.status === 401) {
              return of({ status: 401 } as unknown as HttpResponse<ManifestPayload>);
            }
            return of(null);
          }),
        ),
    );

    if (!response) return { kind: 'error' };
    if (response.status === 304) return { kind: 'not-modified' };
    if (response.status === 401) return { kind: 'unauthorized' };

    const body = response.body;
    if (!body) return { kind: 'error' };

    const modules =
      (body as TenantModulesResponse).modules ??
      (body as { data: TenantModulesResponse }).data?.modules ??
      [];
    const etag = response.headers?.get('ETag') ?? null;
    return { kind: 'changed', modules, etag };
  }

  private async persist(modules: TenantModule[], etag: string | null): Promise<void> {
    if (!this.dbPromise) return;
    const entry: TenantModulesCacheEntry = {
      modules,
      fetched_at: Date.now(),
      tenant: this.tenantFromHostname(),
      etag,
    };
    try {
      const db = await this.dbPromise;
      await db.put(STORE, entry, ENTRY_KEY);
    } catch (err) {
      // Most common cause: user manually deleted the *database* (not just
      // the entry) in DevTools, leaving the cached dbPromise pointing at a
      // stale handle. Surfaced to console so cache-write failures don't go
      // silent during dev.
      console.warn('[TenantModuleService] persist failed:', err);
    }
  }

  // --- private: cross-tab + polling ----------------------------------------

  private openBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') return;
    this.bc = new BroadcastChannel(BC_NAME);
    this.bc.onmessage = (e) => {
      const incoming = (e.data as { etag?: string | null } | undefined)?.etag ?? null;
      if (incoming && incoming !== this.etag) {
        this._updateAvailable.set(true);
      }
    };
  }

  private scheduleNextPoll(): void {
    if (this.pollingStopped) return;
    const delay = POLL_INTERVAL_MS + Math.floor(Math.random() * POLL_JITTER_MS);
    this.pollTimer = setTimeout(() => {
      void this.pollAndStashIfChanged().finally(() => this.scheduleNextPoll());
    }, delay);
  }

  // --- private: helpers ----------------------------------------------------

  private setModules(modules: TenantModule[]): void {
    this._modules.set(modules);
    this.byKey.clear();
    for (const m of modules) this.byKey.set(m.key, m);
  }

  private tenantFromHostname(): string {
    if (typeof window === 'undefined') return 'unknown';
    const [first] = window.location.hostname.split('.');
    return first || 'unknown';
  }

  private migrateLegacyLocalStorage(): void {
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(LEGACY_LS_KEY) !== null) {
        localStorage.removeItem(LEGACY_LS_KEY);
      }
    } catch {
      // ignore
    }
  }
}
